/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import {
    CacheType,
    createObservableDataAction,
    IAction,
    IActionContext,
    IActionInput,
    IAny,
    ICreateActionContext,
    IGeneric
} from '@msdyn365-commerce/core';
import { DateRefinerOptionItem } from '../common/refiner-option';

/**
 * Gets the date refiner options.
 * @returns - List of refiner options.
 */
const getDateRefinerOptions = () => async (ctx: IActionContext): Promise<DateRefinerOptionItem[]> => {
    return Promise.resolve([
        DateRefinerOptionItem.LastSevenDays,
        DateRefinerOptionItem.LastThirtyDays,
        DateRefinerOptionItem.LastThreeMonths,
        DateRefinerOptionItem.LastSixMonths,
        DateRefinerOptionItem.Customized
    ]);
};

/**
 * Action input.
 */
export class GetDateRefinerOptionsInput implements IActionInput {
    public getCacheKey = () => 'OrderHistoryWithSearchAndFiltersDateRefinerOptions';

    public getCacheObjectType = () => 'OrderHistoryWithSearchAndFiltersDateRefinerOptions';

    public dataCacheType = (): CacheType => 'instance';
}

/**
 * Creates the input required to make the retail api call.
 * @param inputData - Action Data Input.
 * @returns - Input object.
 */
const createGetDateRefinerOptionsInput = (inputData: ICreateActionContext<IGeneric<IAny>>): IActionInput => {
    return new GetDateRefinerOptionsInput();
};

/**
 * Get action.
 * @param input - Input data.
 * @param context - Action context.
 * @returns - List of refiner options.
 */
export async function getDateRefinerOptionsAction(
    input: GetDateRefinerOptionsInput,
    context: IActionContext
): Promise<DateRefinerOptionItem[]> {
    if (!context) {
        throw new Error('getDateRefinerOptionsAction - Action context cannot be null/undefined');
    }

    let refinerOptions: DateRefinerOptionItem[] = [];

    try {
        refinerOptions = await getDateRefinerOptions()(context);
    } catch (error) {
        throw error;
    }

    return refinerOptions;
}

/**
 * Get date refiner options action.
 * @param id - Unique Id for data action.
 * @param context - Action context.
 * @param input - Input for data action.
 * @returns Api response.
 */
export const getDateRefinerOptionsActionDataAction = createObservableDataAction<DateRefinerOptionItem[]>({
    id: '@msdyn365-commerce-modules/order-history-with-search-and-filters/get-date-refiner-options',
    action: getDateRefinerOptionsAction as IAction<DateRefinerOptionItem[]>,
    input: createGetDateRefinerOptionsInput
});

export default getDateRefinerOptionsActionDataAction;
